import React from "react";
import "./signatures.css";
export default function SignatureSettings({ field, fields, onChange }) {
  const config = {
    enabled: field.type === "signature-attachment",
    stageSigning: true,
    maxSignatures: Math.max(
      1,
      new Set(
        (field.signature?.signers || []).map((s) =>
          s.currentUser
            ? "SELF"
            : s.userId
              ? `user-${s.userId}`
              : s.email
                ? String(s.email).trim().toLowerCase()
                : JSON.stringify(s),
        ),
      ).size,
    ),
    required: true,
    mode: "PARALLEL",
    deadlineDays: 7,
    waitForCompletion: false,
    allowDownload: true,
    maxSizeMb: 20,
    signers: [{ currentUser: true }],
    signerMode:
      field.signature?.signers?.length > 1 ||
      field.signature?.signers?.some((s) => !s.currentUser)
        ? "CONFIGURED"
        : "CURRENT_USER",
    ...(field.signature || {}),
  };
  const update = (patch) => onChange({ ...config, ...patch });
  const options = fields.filter(
    (f) =>
      !["document", "signature-attachment", "table", "file"].includes(f.type),
  );
  const binding = (label, value, onValue) => (
    <label>
      {label}
      <select value={value || ""} onChange={(e) => onValue(e.target.value)}>
        <option value="">Não vincular</option>
        {options.map((f) => (
          <option value={f.id} key={f.id}>
            {f.label}
          </option>
        ))}
      </select>
    </label>
  );
  const signer = (index, patch) =>
    update({
      signers: config.signers.map((s, i) =>
        i === index ? { ...s, ...patch } : s,
      ),
    });
  return (
    <section className="signature-settings">
      <h3>Assinatura eletrônica</h3>
      <label className="signature-check">
        <input
          type="checkbox"
          checked={config.enabled}
          onChange={(e) => update({ enabled: e.target.checked })}
        />
        {field.type === "document"
          ? "Exigir assinatura eletrônica no documento gerado"
          : "Solicitar assinatura do anexo"}
      </label>
      {config.enabled && (
        <>
          <p>
            PDF com aceite expresso. O método exigido é conferido pelo servidor.
          </p>
          <label>
            Modalidade exigida
            <select
              value={config.signatureMethod || "EMAIL_OTP"}
              onChange={(e) => update({ signatureMethod: e.target.value })}
            >
              <option value="EMAIL_OTP">Código por e-mail</option>
              <option value="PADES_A1">Certificado A1 · PAdES</option>
              <option value="CHOICE">Permitir escolha: e-mail ou A1</option>
              <option value="ICP_BRASIL">
                Qualificada ICP-Brasil · exige homologação
              </option>
            </select>
          </label>
          {config.signatureMethod === "ICP_BRASIL" && (
            <p className="signature-error">
              Esta exigência bloqueia o envio enquanto a homologação ICP-Brasil
              estiver pendente. Não será substituída por e-mail.
            </p>
          )}
          <div className="signature-grid">
            <label>
              Limite de assinaturas por documento
              <input
                type="number"
                min={1}
                max={20}
                value={config.maxSignatures || 1}
                onChange={(e) =>
                  update({
                    maxSignatures: Math.min(
                      20,
                      Math.max(1, Number(e.target.value) || 1),
                    ),
                    stageSigning: true,
                  })
                }
              />
            </label>
            <label>
              Etapas com assinatura
              <select
                value={Array.isArray(config.enabledSteps) ? "selected" : "all"}
                onChange={(e) =>
                  update({
                    enabledSteps: e.target.value === "all" ? null : [0],
                    stageSigning: true,
                  })
                }
              >
                <option value="all">Todas as etapas internas</option>
                <option value="selected">Escolher etapas</option>
              </select>
            </label>
          </div>
          {Array.isArray(config.enabledSteps) && (
            <label>
              Números das etapas (separados por vírgula)
              <input
                defaultValue={config.enabledSteps.map((n) => n + 1).join(", ")}
                onBlur={(e) =>
                  update({
                    enabledSteps: [
                      ...new Set(
                        e.target.value
                          .split(",")
                          .map((n) => Number(n.trim()) - 1)
                          .filter(
                            (n) => Number.isInteger(n) && n >= 0 && n < 200,
                          ),
                      ),
                    ],
                    stageSigning: true,
                  })
                }
              />
            </label>
          )}
          <div className="signature-grid">
            {config.signerMode === "CONFIGURED" && (
              <label>
                Ordem
                <select
                  value={config.mode}
                  onChange={(e) => update({ mode: e.target.value })}
                >
                  <option value="PARALLEL">Simultânea</option>
                  <option value="SEQUENTIAL">Sequencial</option>
                </select>
              </label>
            )}
            <label>
              Prazo em dias
              <input
                type="number"
                min={1}
                max={365}
                value={config.deadlineDays}
                onChange={(e) =>
                  update({ deadlineDays: Number(e.target.value) })
                }
              />
            </label>
            <label>
              Tamanho máximo (MB)
              <input
                type="number"
                min={1}
                max={20}
                value={config.maxSizeMb}
                onChange={(e) => update({ maxSizeMb: Number(e.target.value) })}
              />
            </label>
          </div>
          <label className="signature-check">
            <input
              type="checkbox"
              checked={config.required !== false}
              onChange={(e) => update({ required: e.target.checked })}
            />
            Documento assinado obrigatório para prosseguir
          </label>
          <label className="signature-check">
            <input
              type="checkbox"
              checked={config.waitForCompletion}
              onChange={(e) => update({ waitForCompletion: e.target.checked })}
            />
            Salvar etapa em espera e continuar automaticamente após todas as
            assinaturas
          </label>
          <label className="signature-check">
            <input
              type="checkbox"
              checked={config.allowDownload}
              onChange={(e) => update({ allowDownload: e.target.checked })}
            />
            Permitir baixar PDF final no formulário
          </label>
          <p>
            As notificações são enviadas automaticamente pelo SMTP da
            organização.
          </p>
          <label>
            Quem precisa assinar?
            <select
              value={config.signerMode}
              onChange={(e) =>
                update({ signerMode: e.target.value, placements: [] })
              }
            >
              <option value="CURRENT_USER">
                Somente quem preenche · 1 assinatura
              </option>
              <option value="CONFIGURED">Definir usuários cadastrados</option>
            </select>
          </label>
          {config.signerMode === "CONFIGURED" && (
            <>
              <h4>Destinatários ({config.signers.length})</h4>
              {config.signers.map((s, i) => (
                <fieldset key={i}>
                  <legend>Assinante {i + 1}</legend>
                  <label className="signature-check">
                    <input
                      type="checkbox"
                      checked={s.currentUser === true}
                      onChange={(e) =>
                        signer(i, { currentUser: e.target.checked })
                      }
                    />
                    Usuário que envia o documento
                  </label>
                  {!s.currentUser && (
                    <>
                      <div className="signature-grid">
                        <label>
                          Limite de assinaturas por documento
                          <input
                            type="number"
                            min={1}
                            max={20}
                            value={config.maxSignatures || 1}
                            onChange={(e) =>
                              update({
                                maxSignatures: Math.min(
                                  20,
                                  Math.max(1, Number(e.target.value) || 1),
                                ),
                                stageSigning: true,
                              })
                            }
                          />
                        </label>
                        <label>
                          Etapas com assinatura
                          <select
                            value={
                              Array.isArray(config.enabledSteps)
                                ? "selected"
                                : "all"
                            }
                            onChange={(e) =>
                              update({
                                enabledSteps:
                                  e.target.value === "all" ? null : [0],
                                stageSigning: true,
                              })
                            }
                          >
                            <option value="all">
                              Todas as etapas internas
                            </option>
                            <option value="selected">Escolher etapas</option>
                          </select>
                        </label>
                      </div>
                      {Array.isArray(config.enabledSteps) && (
                        <label>
                          Números das etapas (separados por vírgula)
                          <input
                            defaultValue={config.enabledSteps
                              .map((n) => n + 1)
                              .join(", ")}
                            onBlur={(e) =>
                              update({
                                enabledSteps: [
                                  ...new Set(
                                    e.target.value
                                      .split(",")
                                      .map((n) => Number(n.trim()) - 1)
                                      .filter(
                                        (n) =>
                                          Number.isInteger(n) &&
                                          n >= 0 &&
                                          n < 200,
                                      ),
                                  ),
                                ],
                                stageSigning: true,
                              })
                            }
                          />
                        </label>
                      )}
                      <div className="signature-grid">
                        {binding("Campo do nome", s.nameField, (v) =>
                          signer(i, { nameField: v }),
                        )}
                        {binding("Campo do e-mail", s.emailField, (v) =>
                          signer(i, { emailField: v }),
                        )}
                        {binding(
                          "Campo do CPF/CNPJ (necessário para A1)",
                          s.cpfField,
                          (v) => signer(i, { cpfField: v }),
                        )}
                        {binding(
                          "Campo do telefone (opcional)",
                          s.phoneField,
                          (v) => signer(i, { phoneField: v }),
                        )}
                      </div>
                      <label>
                        Nome fixo (se não usar campo)
                        <input
                          value={s.name || ""}
                          onChange={(e) => signer(i, { name: e.target.value })}
                        />
                      </label>
                      <label>
                        E-mail fixo (se não usar campo)
                        <input
                          type="email"
                          value={s.email || ""}
                          onChange={(e) => signer(i, { email: e.target.value })}
                        />
                      </label>
                    </>
                  )}
                  {binding(
                    "Pedir esta assinatura somente quando",
                    s.when?.fieldId,
                    (v) =>
                      signer(i, {
                        when: { fieldId: v, equals: s.when?.equals || "" },
                      }),
                  )}
                  {s.when?.fieldId && (
                    <label>
                      Valor da condição
                      <input
                        value={s.when.equals || ""}
                        onChange={(e) =>
                          signer(i, {
                            when: { ...s.when, equals: e.target.value },
                          })
                        }
                      />
                    </label>
                  )}
                  <button
                    type="button"
                    disabled={config.signers.length === 1}
                    onClick={() =>
                      update({
                        signers: config.signers.filter(
                          (_, index) => i !== index,
                        ),
                        placements: [],
                      })
                    }
                  >
                    Remover assinante
                  </button>
                </fieldset>
              ))}
              <button
                type="button"
                disabled={config.signers.length >= 20}
                onClick={() =>
                  update({
                    signers: [...config.signers, { currentUser: false }],
                  })
                }
              >
                Adicionar usuário do sistema
              </button>
            </>
          )}
          <p>
            As posições são definidas visualmente ao enviar o PDF. A ordem acima
            é usada no fluxo sequencial.
          </p>
          {binding(
            "Exigir assinatura somente quando este campo tiver o valor indicado",
            config.requiredWhen?.fieldId,
            (v) =>
              update({
                requiredWhen: {
                  fieldId: v,
                  equals: config.requiredWhen?.equals || "",
                },
              }),
          )}
          {config.requiredWhen?.fieldId && (
            <label>
              Valor esperado
              <input
                value={config.requiredWhen?.equals || ""}
                onChange={(e) =>
                  update({
                    requiredWhen: {
                      ...config.requiredWhen,
                      equals: e.target.value,
                    },
                  })
                }
              />
            </label>
          )}
          <label>
            Mensagem aos assinantes
            <textarea
              value={config.message || ""}
              maxLength={2000}
              onChange={(e) => update({ message: e.target.value })}
            />
          </label>
        </>
      )}
    </section>
  );
}
